import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { api, ymd } from "@/lib/api";
import { useLookups } from "@/lib/useLookups";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { PageHeader, Panel, GoldButton, GhostButton, Select } from "@/components/common";
import { WaTemplateDialog } from "@/components/WaTemplateDialog";
import { MessageCircle } from "lucide-react";

const START_H = 8;
const END_H = 21;
const ROW = 60;
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const mondayOf = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const ApptBlock = ({ a, onClick }) => {
  const [h, m] = a.start.slice(11, 16).split(":").map(Number);
  const top = ((h - START_H) * 60 + m) * (ROW / 60);
  const height = Math.max(a.duration * (ROW / 60) - 2, 22);
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(a); }} data-testid={`cal-appt-${a.id}`}
      className={`absolute left-1 right-1 overflow-hidden rounded-lg border-l-4 bg-white px-2 py-1 text-left shadow-sm transition-transform hover:scale-[1.02] hover:z-10 ${a.status === "annullato" ? "opacity-50 line-through" : ""}`}
      style={{ top, height, borderLeftColor: a.operator_color, background: `${a.operator_color}1A` }}>
      <div className="text-[11px] font-semibold text-[#121212] truncate">{a.start.slice(11, 16)} {a.client_name}</div>
      <div className="text-[10px] text-[#6E675F] truncate">{a.treatment_name}{a.operator_name ? ` · ${a.operator_name}` : ""}</div>
    </button>
  );
};

export default function CalendarPage() {
  const lk = useLookups();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [view, setView] = useState(isMobile ? "day" : "week");
  const [anchor, setAnchor] = useState(new Date());
  const [opFilter, setOpFilter] = useState("");
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState({ open: false });
  const [waOpen, setWaOpen] = useState(false);

  const days = useMemo(() => (view === "week" ? Array.from({ length: 7 }, (_, i) => addDays(mondayOf(anchor), i)) : [new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate())]), [view, anchor]);
  const load = useCallback(() => {
    api.get("/appointments", { params: { start: ymd(days[0]), end: ymd(addDays(days[days.length - 1], 1)), operator_id: opFilter } }).then((r) => setItems(r.data));
  }, [days, opFilter]);
  useEffect(() => { load(); }, [load]);

  const shift = (dir) => setAnchor(addDays(anchor, dir * (view === "week" ? 7 : 1)));
  const hours = Array.from({ length: END_H - START_H }, (_, i) => START_H + i);
  const title = view === "week"
    ? `${days[0].toLocaleDateString("it-IT", { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}`
    : anchor.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  const slotClick = (day, e) => {
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const mins = Math.floor((y / ROW) * 60 / 30) * 30 + START_H * 60;
    setDlg({ open: true, defaultStart: `${ymd(day)}T${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}` });
  };
  const todayStr = ymd(new Date());

  return (
    <div data-testid="calendar-page">
      <PageHeader overline="Agenda" title="Calendario">
        <GhostButton onClick={() => setWaOpen(true)} data-testid="wa-template-button"><MessageCircle className="h-4 w-4" /> Testo WhatsApp</GhostButton>
        <GoldButton onClick={() => setDlg({ open: true, defaultStart: `${ymd(anchor)}T09:00` })} data-testid="new-appointment-button"><Plus className="h-4 w-4" /> Nuovo appuntamento</GoldButton>
      </PageHeader>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <GhostButton onClick={() => shift(-1)} className="px-2.5" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></GhostButton>
          <GhostButton onClick={() => setAnchor(new Date())} data-testid="cal-today">Oggi</GhostButton>
          <GhostButton onClick={() => shift(1)} className="px-2.5" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></GhostButton>
        </div>
        <div className="font-serif text-2xl capitalize" data-testid="cal-title">{title}</div>
        <div className="ml-auto flex items-center gap-2">
          <Select value={opFilter} onChange={(e) => setOpFilter(e.target.value)} placeholder="Tutti gli operatori" options={lk.operators.map((o) => ({ value: o.id, label: o.name }))} className="bg-white py-2 w-48" data-testid="cal-operator-filter" />
          <div className="flex rounded-full border border-[#E8E1D7] bg-white p-1">
            {[["day", "Giorno"], ["week", "Settimana"]].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`} className={`rounded-full px-3 py-1 text-sm transition-colors ${view === v ? "bg-[#121212] text-[#F6F1EA]" : "text-[#6E675F]"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      <Panel className="overflow-x-auto">
        <div className="min-w-[720px]" style={view === "day" ? { minWidth: 0 } : undefined}>
          <div className="grid border-b border-[#E8E1D7] bg-[#F6F1EA]" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)` }}>
            <div />
            {days.map((d) => (
              <div key={d} className={`px-2 py-3 text-center ${ymd(d) === todayStr ? "text-[#C5A059]" : "text-[#24211E]"}`}>
                <div className="text-[10px] uppercase tracking-[0.2em]">{DAYS[(d.getDay() + 6) % 7]}</div>
                <div className="font-serif text-2xl">{d.getDate()}</div>
              </div>
            ))}
          </div>
          <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)` }}>
            <div>{hours.map((h) => <div key={h} style={{ height: ROW }} className="pr-2 text-right text-[11px] text-[#9E958A] -mt-1.5">{h}:00</div>)}</div>
            {days.map((d) => (
              <div key={d} onClick={(e) => slotClick(d, e)} data-testid={`cal-day-${ymd(d)}`} className={`relative cursor-pointer border-l border-[#E8E1D7] ${ymd(d) === todayStr ? "bg-[#C5A059]/5" : ""}`} style={{ height: ROW * hours.length }}>
                {hours.map((h) => <div key={h} style={{ height: ROW }} className="border-b border-[#E8E1D7]/70 hover:bg-[#F6F1EA]/60" />)}
                {items.filter((a) => a.start.slice(0, 10) === ymd(d)).map((a) => <ApptBlock key={a.id} a={a} onClick={(x) => setDlg({ open: true, initial: x })} />)}
              </div>
            ))}
          </div>
        </div>
      </Panel>
      {lk.operators.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#6E675F]">
          {lk.operators.map((o) => <span key={o.id} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: o.color }} />{o.name}</span>)}
        </div>
      )}
      <AppointmentDialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })} initial={dlg.initial} defaultStart={dlg.defaultStart} onSaved={load} clients={lk.clients} treatments={lk.treatments} operators={lk.operators} />
      <WaTemplateDialog open={waOpen} onOpenChange={setWaOpen} />
    </div>
  );
}
